import React, { useEffect, useMemo, useState } from 'react';
import { useNetworkElementsStore } from '../../../../../store/networkElementsStore';
import { useNetworkUIStore } from '../../../../../store/networkUIStore';
import { useSettingsStore } from '../../../../../store/settingsStore';
import type { Abgangsleitung, TrafoStation } from '../../../../../types/network';
import { fmt } from '../../../../../utils/numberFormat';
import { puToVolts } from '../../../../../utils/electricalConstants';
import { cableDisplayName } from '../../../../../utils/elementDisplayName';
import { parseUtcTimestamp, formatInstantLabel as timeLabel } from '../../../../../utils/timeUtils';
import { findCablesConnectedToTrafo, findSegmentsConnectedToTrafo } from '../../../../../utils/networkConnectionUtils';
import { fetchMeasuredTrafoAt, type MeasuredTrafoAt } from '../../../../../services/elementHistoryService';

/**
 * "Elektrische Größen" of a transformer station — everything the system
 * knows per phase at the shown instant, with one rule: a MEASUREMENT wins,
 * the CALCULATION fills the gaps and is shown next to it for comparison.
 *
 *  - measured: trafo_measurement_history (per-phase U/I, f, cos φ) at the
 *    playback instant (or now, in live mode)
 *  - calculated: the powerflow result held in the store — per-phase bus
 *    voltage, per-phase LV current and active power (raw solver values,
 *    never the totals/3 fallback of `phases.*`)
 *  - per feeder (Abgang): P, per-phase I and loading of the cable at the
 *    trafo busbar, from the same powerflow result; click selects the cable
 *
 * German live zones with Lemonbeat show their measured phase table in
 * LemonbeatMeasurementsSection below; this section then contributes the
 * calculated view and the feeder table.
 */

interface Props {
  trafo: TrafoStation;
  isHistorical: boolean;
  /** Playback instant (ISO, UTC) — null in live mode. */
  effectiveTs: string | null;
}

type Source = 'measured' | 'calculated';

const SourceDot: React.FC<{ source: Source; className?: string }> = ({ source, className = '' }) => (
  <span
    className={`inline-block w-1.5 h-1.5 rounded-full align-middle ${source === 'measured' ? 'bg-teal-500' : 'bg-blue-400'} ${className}`}
    aria-hidden
  />
);

const SourceTag: React.FC<{ source: Source }> = ({ source }) => (
  <span
    className={`inline-flex items-center gap-1 text-[10px] whitespace-nowrap ${source === 'measured'
      ? 'text-teal-700 dark:text-teal-300'
      : 'text-blue-600 dark:text-blue-300'}`}
  >
    <SourceDot source={source} />
    {source === 'measured' ? 'gemessen' : 'berechnet'}
  </span>
);

interface PhaseRow {
  label: string;
  unit: string;
  digits: number;
  primary: (number | null)[];
  primarySource: Source | null;
  /** Shown muted below the primary value when both sources exist. */
  secondary?: (number | null)[];
}

const fmtVal = (v: number | null, digits: number, unit: string) =>
  v == null || !Number.isFinite(v) ? '—' : `${fmt(v, digits)} ${unit}`;

export const TrafoElectricalSection: React.FC<Props> = ({ trafo, isHistorical, effectiveTs }) => {
  const powerMeasurement = useNetworkElementsStore(s => s.powerMeasurements[trafo.id]);
  const elements = useNetworkElementsStore(s => s.elements);
  const segments = useNetworkElementsStore(s => s.segments);
  const setSelectedElement = useNetworkUIStore(s => s.setSelectedElement);
  const setHighlightedElement = useNetworkUIStore(s => s.setHighlightedElement);
  const cableLevels = useSettingsStore(s => s.thresholds.cableLoadLevels);

  // ── Measured record at the instant ────────────────────────────────────
  const [measured, setMeasured] = useState<MeasuredTrafoAt | null>(null);
  const [measuredLoading, setMeasuredLoading] = useState(false);
  const [liveTick, setLiveTick] = useState(0);

  useEffect(() => {
    if (isHistorical) return;
    const id = window.setInterval(() => setLiveTick(t => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, [isHistorical]);

  useEffect(() => {
    let cancelled = false;
    const ts = isHistorical ? effectiveTs : new Date().toISOString();
    if (!ts) {
      setMeasured(null);
      return;
    }
    setMeasuredLoading(true);
    fetchMeasuredTrafoAt(trafo.id, ts)
      .then(rec => { if (!cancelled) setMeasured(rec); })
      .catch(() => { if (!cancelled) setMeasured(null); })
      .finally(() => { if (!cancelled) setMeasuredLoading(false); });
    return () => { cancelled = true; };
  }, [trafo.id, isHistorical, effectiveTs, liveTick]);

  // ── Calculated per-phase values ───────────────────────────────────────
  const calc = useMemo(() => {
    const pm = powerMeasurement;
    if (!pm) return null;
    const u = [pm.voltageL1Pu, pm.voltageL2Pu, pm.voltageL3Pu].map(v => (v != null ? puToVolts(v) : null));
    const i = [pm.currentL1A ?? null, pm.currentL2A ?? null, pm.currentL3A ?? null];
    const p = [pm.powerL1Kw ?? null, pm.powerL2Kw ?? null, pm.powerL3Kw ?? null];
    const s = pm.totalApparentPowerKva ?? null;
    const pTot = pm.totalActivePower ?? null;
    const cosPhi = s && s > 0 && pTot != null ? Math.min(1, Math.abs(pTot) / s) : null;
    return { u, i, p, cosPhi, at: pm.timestamp ? new Date(pm.timestamp) : null };
  }, [powerMeasurement]);

  const measuredU = measured ? [measured.u1V, measured.u2V, measured.u3V] : null;
  const measuredI = measured ? [measured.i1A, measured.i2A, measured.i3A] : null;
  const hasMeasuredU = !!measuredU && measuredU.some(v => v != null);
  const hasMeasuredI = !!measuredI && measuredI.some(v => v != null);
  const hasCalcU = !!calc && calc.u.some(v => v != null);
  const hasCalcI = !!calc && calc.i.some(v => v != null);
  const hasCalcP = !!calc && calc.p.some(v => v != null);

  const rows: PhaseRow[] = [
    {
      label: 'Spannung L-N',
      unit: 'V',
      digits: 1,
      primary: hasMeasuredU ? measuredU! : (calc?.u ?? [null, null, null]),
      primarySource: hasMeasuredU ? 'measured' : hasCalcU ? 'calculated' : null,
      secondary: hasMeasuredU && hasCalcU ? calc!.u : undefined,
    },
    {
      label: 'Strom',
      unit: 'A',
      digits: 1,
      primary: hasMeasuredI ? measuredI! : (calc?.i ?? [null, null, null]),
      primarySource: hasMeasuredI ? 'measured' : hasCalcI ? 'calculated' : null,
      secondary: hasMeasuredI && hasCalcI ? calc!.i : undefined,
    },
    {
      label: 'Wirkleistung',
      unit: 'kW',
      digits: 1,
      primary: calc?.p ?? [null, null, null],
      primarySource: hasCalcP ? 'calculated' : null,
    },
  ];

  const measuredAtLabel = measured ? timeLabel(parseUtcTimestamp(measured.measuredAt)) : null;
  const calcAtLabel = calc?.at ? timeLabel(calc.at) : null;
  const measuredAgeMin = measured && calc?.at
    ? Math.round((calc.at.getTime() - parseUtcTimestamp(measured.measuredAt).getTime()) / 60_000)
    : null;

  // ── Feeders (Abgänge) at the LV busbar ────────────────────────────────
  const feeders = useMemo(() => {
    const cables = elements.filter((e): e is Abgangsleitung => e.type === 'abgangsleitung');
    const connected = findCablesConnectedToTrafo(trafo, cables);
    const resolve = (id: string) => elements.find(e => e.id === id);
    return connected.map(cable => {
      const trafoSeg = findSegmentsConnectedToTrafo(trafo, cable)[0] ?? cable.segments?.[0];
      const segState = trafoSeg ? segments[trafoSeg.id] : undefined;
      // Sign: positive = the feeder draws from the busbar (Bezug), negative =
      // it feeds back. powerKW is P at the segment's "from" end; when the
      // trafo sits at the "to" end the direction flips (losses neglected).
      const atFrom = trafoSeg?.fromBus != null && trafoSeg.fromBus === trafo.busIndexLv;
      const pRaw = segState?.powerKW ?? null;
      const pKw = pRaw == null ? null : atFrom ? pRaw : -pRaw;
      const loading = Math.max(
        ...cable.segments.map(sg => segments[sg.id]?.loadingPercent ?? -1),
        segState?.loadingPercent ?? -1,
      );
      return {
        cable,
        name: cableDisplayName(cable, resolve),
        pKw,
        i: [segState?.currentL1A ?? null, segState?.currentL2A ?? null, segState?.currentL3A ?? null],
        iTotal: segState?.currentA ?? null,
        loading: loading >= 0 ? loading : null,
      };
    }).sort((a, b) => (b.loading ?? -1) - (a.loading ?? -1));
  }, [elements, segments, trafo]);

  const loadingClass = (v: number | null) => {
    if (v == null) return 'text-gray-500 dark:text-primary-300';
    if (v >= cableLevels.red) return 'text-red-600 dark:text-red-400 font-semibold';
    if (v >= cableLevels.orange) return 'text-amber-600 dark:text-amber-400 font-semibold';
    return 'text-emerald-600 dark:text-emerald-400 font-medium';
  };

  const nothingAtAll = !calc && !measured && !measuredLoading && feeders.length === 0;
  if (nothingAtAll) return null;

  return (
    <div className="bg-white dark:bg-primary-800 rounded-md shadow-sm border border-gray-200 dark:border-primary-600 overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 dark:bg-primary-700 border-b border-gray-200 dark:border-primary-600">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-bold text-gray-900 dark:text-white flex items-center">
            <svg className="w-4 h-4 mr-2 text-gray-700 dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Elektrische Größen
          </h3>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-gray-500 dark:text-primary-300">
          <span className="inline-flex items-center gap-1">
            <SourceDot source="measured" />
            {measured
              ? `gemessen · Trafo-Messung ${measuredAtLabel ?? ''}${measured.source ? ` (${measured.source})` : ''}`
              : measuredLoading ? 'gemessen · wird geladen …' : 'gemessen · keine Trafo-Messung zu diesem Zeitpunkt'}
          </span>
          <span className="inline-flex items-center gap-1">
            <SourceDot source="calculated" />
            {calc ? `berechnet · Lastfluss ${calcAtLabel ?? ''}` : 'berechnet · kein Lastfluss-Ergebnis'}
          </span>
        </div>
        {measuredAgeMin != null && Math.abs(measuredAgeMin) > 5 && (
          <div className="mt-0.5 text-[10px] text-gray-400 dark:text-primary-400">
            Messung liegt {Math.abs(measuredAgeMin)} min {measuredAgeMin > 0 ? 'vor' : 'nach'} dem Berechnungszeitpunkt (10-min-Raster der Messung).
          </div>
        )}
      </div>

      {/* Per-phase table */}
      <div className="px-3 py-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-primary-300">
              <th className="text-left font-semibold py-1 pr-2">Größe</th>
              <th className="text-right font-semibold py-1 px-1">L1</th>
              <th className="text-right font-semibold py-1 px-1">L2</th>
              <th className="text-right font-semibold py-1 px-1">L3</th>
              <th className="text-right font-semibold py-1 pl-2">Quelle</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.label} className="border-t border-gray-100 dark:border-primary-700 align-top">
                <td className="py-1.5 pr-2 text-gray-700 dark:text-primary-200 whitespace-nowrap">{row.label}</td>
                {row.primary.map((v, idx) => (
                  <td key={idx} className="py-1.5 px-1 text-right tabular-nums">
                    <div className="font-semibold text-gray-900 dark:text-white">{fmtVal(v, row.digits, row.unit)}</div>
                    {row.secondary && (
                      <div
                        className="text-[10px] text-blue-600/80 dark:text-blue-300/80"
                        title="Lastfluss-Wert zum Vergleich"
                      >
                        ber. {fmtVal(row.secondary[idx], row.digits, row.unit)}
                      </div>
                    )}
                  </td>
                ))}
                <td className="py-1.5 pl-2 text-right">
                  {row.primarySource ? <SourceTag source={row.primarySource} /> : <span className="text-[10px] text-gray-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Scalars */}
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="rounded-md bg-gray-50 dark:bg-primary-700/60 px-2 py-1.5">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-primary-300">cos φ</div>
            {measured?.cosPhi != null ? (
              <div className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                {fmt(Math.abs(measured.cosPhi), 3)}
                <span className="ml-1 text-[10px] font-normal text-gray-500 dark:text-primary-300">
                  {measured.cosPhi < 0 ? 'Rückspeisung' : ''}
                </span>
                <span className="ml-1"><SourceTag source="measured" /></span>
                {calc?.cosPhi != null && (
                  <div className="text-[10px] font-normal text-blue-600/80 dark:text-blue-300/80">ber. {fmt(calc.cosPhi, 3)}</div>
                )}
              </div>
            ) : calc?.cosPhi != null ? (
              <div className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                {fmt(calc.cosPhi, 3)} <span className="ml-1"><SourceTag source="calculated" /></span>
              </div>
            ) : (
              <div className="text-sm text-gray-400">—</div>
            )}
          </div>
          <div className="rounded-md bg-gray-50 dark:bg-primary-700/60 px-2 py-1.5">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-primary-300">Frequenz</div>
            {measured?.frequencyHz != null ? (
              <div className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                {fmt(measured.frequencyHz, 2)} Hz <span className="ml-1"><SourceTag source="measured" /></span>
              </div>
            ) : (
              <div className="text-sm text-gray-400" title="Die Frequenz ist nur messbar, nicht Teil des Lastflusses">—</div>
            )}
          </div>
        </div>

        {calc && !hasCalcI && !hasCalcP && (
          <div className="mt-2 text-[10px] text-gray-500 dark:text-primary-300">
            Für diesen Berechnungsstand liegen keine Phasenströme/-leistungen des Trafos vor
            {isHistorical ? ' (älterer Archivstand)' : ''}.
          </div>
        )}
      </div>

      {/* Feeders */}
      {feeders.length > 0 && (
        <div className="border-t border-gray-200 dark:border-primary-600 px-3 py-2">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-600 dark:text-primary-300">
              Abgänge an der Sammelschiene ({feeders.length})
            </div>
            <SourceTag source="calculated" />
          </div>
          {/* table-fixed: with auto layout the name column ignores its
              max-width (truncate never fires on a td), the table grows wider
              than the panel and the Ausl. column gets clipped at the edge. */}
          <table className="w-full text-xs table-fixed">
            <colgroup>
              <col />
              <col className="w-18" />
              <col className="w-28" />
              <col className="w-14" />
            </colgroup>
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-primary-300">
                <th className="text-left font-semibold py-1 pr-2">Abgang</th>
                <th className="text-right font-semibold py-1 px-1">P</th>
                <th className="text-right font-semibold py-1 px-1 whitespace-nowrap">I L1 / L2 / L3</th>
                <th className="text-right font-semibold py-1 pl-1">Ausl.</th>
              </tr>
            </thead>
            <tbody>
              {feeders.map(f => (
                <tr
                  key={f.cable.id}
                  className="border-t border-gray-100 dark:border-primary-700 hover:bg-gray-50 dark:hover:bg-primary-700/60 cursor-pointer"
                  onClick={() => { setSelectedElement(f.cable); setHighlightedElement(f.cable.id); }}
                  title="Abgang auswählen"
                >
                  <td className="py-1.5 pr-2 text-gray-800 dark:text-primary-100 truncate" title={f.name}>{f.name}</td>
                  <td className="py-1.5 px-1 text-right tabular-nums text-gray-900 dark:text-white whitespace-nowrap">
                    {f.pKw == null ? '—' : `${fmt(f.pKw, 1)} kW`}
                  </td>
                  <td className="py-1.5 px-1 text-right tabular-nums text-gray-700 dark:text-primary-200 whitespace-nowrap">
                    {f.i.every(v => v == null)
                      ? (f.iTotal == null ? '—' : `${fmt(f.iTotal, 0)} A`)
                      : `${f.i.map(v => (v == null ? '—' : fmt(v, 0))).join(' / ')} A`}
                  </td>
                  <td className={`py-1.5 pl-1 text-right tabular-nums whitespace-nowrap ${loadingClass(f.loading)}`}>
                    {f.loading == null ? '—' : `${fmt(f.loading, 1)} %`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
